import { ParsedUrlQuery } from 'querystring';

import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { Box, Paper, Stack, Typography } from '@mui/material';

import invariant from 'invariant';
import omit from 'lodash/omit';
import { useRouter } from 'next/router';

import { Analytics } from '@watershed/analytics/analyticsUtils';
import Callout from '@watershed/shared-frontend/components/Callout';
import LoginHeader from '@watershed/shared-frontend/components/LoginHeader';
import usePrefetchRoute from '@watershed/shared-frontend/hooks/usePrefetchRoute';
import getQueryStringRedirect from '@watershed/shared-frontend/utils/getQueryStringRedirect';
import { submitEmailPassword } from '@watershed/shared-frontend/utils/loginUtils';
import { AuthError } from '@watershed/shared-universal/authConstants';
import {
  LOGIN_CUSTOMER_RELATION_OPTIONS,
  LoginCustomerRelationOption,
  LoginQueryParams,
  routeForCustomerHub,
  routeForReport,
  SUPPORT_EMAIL_ADDRESS,
  SUPPORT_EMAIL_LINK,
} from '@watershed/shared-universal/dashboardRoutes';
import { parseQueryParam } from '@watershed/shared-universal/utils/queryParamUtils';
import stringToEnum from '@watershed/shared-universal/utils/stringToEnum';
import assertNever from '@watershed/shared-util/src/assertNever';
import ErrorBox from '@watershed/ui-core/components/ErrorBox';
import TextLink from '@watershed/ui-core/components/TextLink';

import {
  EmailFormWithDomains,
  SupplierLoginPageContainer,
} from '../../surveys/SupplierLoginPage';
import { useLoginState } from '../hooks/useLoginState';
import { useRecipientMetadata } from '../hooks/useRecipientMetadata';
import { centeredCardSx, loginPaperWrapperSx } from '../styles';
import { LoginState } from '../types';
import { sendMagicLink } from '../utils/fetchUtils';
import AuthMethodList from './AuthMethodList';
import {
  DisabledEmailFieldWithChange,
  LoginFooter,
  MagicLinkPendingSection,
  PasswordAuthSection,
} from './AuthMethods';
import { EmailForm } from './EmailForm';
import LoginMessages from './LoginMessages';
import { getAuthErrorMessageOrDefault } from '../constants';

/**
 * This whole notion of Login state is about starting with collecting a user's
 * email and then making a query to determine what login options are appropriate
 * for that user.
 *
 * The initial state for a new user is an empty form asking for the email, but
 * once a user has logged in, we do things like set the email to local storage
 * so that it persists over time.
 *
 * See workspaces/server/routes/auth/email.ts for information on how we gather
 * all of this.
 */

function useCustomerRelation(): LoginCustomerRelationOption | null {
  const router = useRouter();
  const customerRelation = parseQueryParam(router.query.customerRelation);

  if (customerRelation === null) {
    return null;
  }

  return stringToEnum(LOGIN_CUSTOMER_RELATION_OPTIONS, customerRelation, null);
}

function logoutReasonFromQuery(query: ParsedUrlQuery) {
  const reasonString = parseQueryParam(query.reason);
  if (reasonString === 'idle-timeout') {
    return (
      <Trans context="Explanation for why a user was logged out.">
        You have been logged out due to inactivity.
      </Trans>
    );
  }
  return undefined;
}

const EMAIL_LOCALSTORAGE_KEY = 'loginEmail';

type LoginPageProps = {
  authError: string | null;
  email: string | null;
  adminName: string | null;
  adminEmail: string | null;
  watershedGeneric: string | null;
};

export default function LoginPage({
  authError,
  email: emailFromQuery,
  adminName,
  adminEmail,
  watershedGeneric,
}: LoginPageProps) {
  const router = useRouter();
  // Prefetch the index and home experiences right out of the gates
  usePrefetchRoute('/');
  usePrefetchRoute('/home');

  const query: LoginQueryParams = router.query;
  const customerCompanyId = parseQueryParam(query.customerId);
  const recipientCompanyId = parseQueryParam(query.recipientCompanyId);
  const customerRelation = useCustomerRelation();

  const [submittedHelpRequest, setSubmittedHelpRequest] = useState(false);

  const {
    state,
    setState,
    fetchEmailMetadataAndSetState,
    metadata,
    customerName,
  } = useLoginState({
    authError,
    emailFromQuery,
    customerCompanyId,
    recipientCompanyId,
  });

  const recipientMetadata = useRecipientMetadata({
    customerRelation,
    customerCompanyId,
    recipientCompanyId,
  });

  const queryStringRedirect = getQueryStringRedirect(router.query) || '';
  const reasonMsg = logoutReasonFromQuery(router.query);
  const isTryingToAccessReport = queryStringRedirect.includes(
    routeForReport('')
  );
  const isTryingToAccessCustomerHub = queryStringRedirect.includes(
    routeForCustomerHub('')
  );

  // The error is a raw string, and we'll localize it when rendering the error message.
  const onError = (error: string) => {
    setState((state) => ({ ...state, error }));
  };

  const handleChangeEmail = () => {
    setState({
      state: LoginState.Email,
      email: '',
      fetching: false,
    });
    void router.push({
      pathname: location.pathname,
      query: omit(router.query, ['email', 'authError']),
      hash: location.hash,
    });
  };

  const handleLoginWithPassword = () => {
    invariant(
      state.state === LoginState.LoginMethodSelection,
      'handleLoginWithPassword state'
    );
    setState({
      state: LoginState.PasswordMethodSelected,
      email: state.email,
      metadata: state.metadata,
      fetching: false,
    });
    Analytics.action('login.loginWithPasswordOnWorkos', {
      email: state.email,
    });
  };

  const handleSubmitEmail = async (email: string) => {
    localStorage.setItem(EMAIL_LOCALSTORAGE_KEY, email);
    void router.push({
      pathname: location.pathname,
      query: {
        ...omit(router.query, 'authError'),
        email,
      },
      hash: location.hash,
    });

    await fetchEmailMetadataAndSetState(email, undefined);
  };

  const handleSubmitPassword = async (password: string, email: string) => {
    invariant(
      state.state === LoginState.PasswordMethodSelected,
      'state is password'
    );
    setState((state) => ({ ...state, fetching: true }));
    try {
      const result = await submitEmailPassword({
        email,
        password,
        shouldSkipOrgChooser: false,
        targetOrgId: state.metadata?.orgId,
      });

      setState((state) => ({ ...state, fetching: false }));
      const redirectPath =
        result?.redirect ?? (getQueryStringRedirect(router.query) || '/');
      // We do a hard page-load (instead of a client-side redirect) to avoid
      // potential bugs related to authentication state by refreshing that state
      // completely.
      window.location.replace(redirectPath);
    } catch (ex) {
      const error = ex as Error;
      Analytics.error('submitAuth', {
        error: error.message,
      });
      setState((state) => ({
        ...state,
        error: error.message,
        fetching: false,
      }));
    }
  };

  const handleSendMagicLink = async () => {
    invariant(state.email, 'email is present');
    invariant('metadata' in state && state.metadata, 'metadata is present');
    Analytics.action('login.sendMagicLink', { email: state.email });

    setState((state) => ({ ...state, fetching: true }));

    try {
      await sendMagicLink({
        email: state.email,
        targetOrgId: state.metadata.orgId,
        redirect: getQueryStringRedirect(router.query),
        shouldSkipOrgChooser: false,
      });
      setState({
        state: LoginState.MagicLinkPending,
        email: state.email,
        metadata: state.metadata,
      });
    } catch (err) {
      const error = err as Error;
      setState((state) => ({
        ...state,
        fetching: false,
        error: error.message,
      }));
    }
  };

  const LoginComponent = () => {
    switch (state.state) {
      case LoginState.Email:
        return customerRelation === LOGIN_CUSTOMER_RELATION_OPTIONS.SUPPLIER &&
          recipientMetadata.orgDomains &&
          recipientMetadata.orgDomains.length > 0 ? (
          <EmailFormWithDomains
            state={state}
            domains={recipientMetadata.orgDomains}
            onSubmit={handleSubmitEmail}
            onError={onError}
            recipientMetadata={recipientMetadata}
            setSubmittedHelpRequest={setSubmittedHelpRequest}
          />
        ) : (
          <EmailForm
            state={state}
            onSubmit={handleSubmitEmail}
            onError={onError}
          />
        );
      case LoginState.LoginMethodSelection:
        return (
          <>
            <DisabledEmailFieldWithChange
              emailAddress={state.email}
              onReset={handleChangeEmail}
            />
            <AuthMethodList
              email={state.email}
              orgMetadata={state.metadata}
              fetching={state.fetching}
              handleLoginWithPassword={handleLoginWithPassword}
              handleSendMagicLink={handleSendMagicLink}
              targetOrgId={state.metadata?.orgId}
              shouldSkipOrgChooser={false}
            />
          </>
        );
      case LoginState.PasswordMethodSelected:
        return (
          <>
            <DisabledEmailFieldWithChange
              emailAddress={state.email}
              onReset={handleChangeEmail}
            />
            <PasswordAuthSection
              email={state.email}
              onSubmit={handleSubmitPassword}
              fetching={state.fetching}
              isDataPortalLogin={state.metadata?.isDataPortalLogin}
            />
          </>
        );

      case LoginState.MagicLinkPending:
        return (
          <MagicLinkPendingSection
            emailAddress={state.email}
            onReset={handleChangeEmail}
          />
        );
      case LoginState.Error:
        return (
          <DisabledEmailFieldWithChange
            emailAddress={state.email}
            onReset={handleChangeEmail}
          />
        );
      default:
        assertNever(state);
    }
  };

  return (
    <Box sx={loginPaperWrapperSx}>
      {isTryingToAccessCustomerHub &&
      customerRelation &&
      customerRelation === LOGIN_CUSTOMER_RELATION_OPTIONS.SUPPLIER ? (
        <Paper
          sx={{
            width: '800px',
            height: '600px',
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <Box display="flex" flexDirection="column" sx={{ m: 4, mr: 0 }}>
            {state.error && authError !== AuthError.PermissionsRequired && (
              <ErrorBox marginBottom={2} marginRight={4}>
                {getAuthErrorMessageOrDefault(state.error)}
              </ErrorBox>
            )}
            <SupplierLoginPageContainer
              recipientName={recipientMetadata.orgName ?? null}
              // Fix this
              rootName={recipientMetadata.customerName ?? null}
              setState={setState}
              LoginComponent={LoginComponent}
              submittedHelpRequest={submittedHelpRequest}
              state={state}
            />
          </Box>
          <Box
            display="flex"
            sx={{
              px: 2,
              py: 2,
              gap: 2,
              bottom: 0,
              position: 'absolute',
            }}
          >
            <LoginFooter />
          </Box>
        </Paper>
      ) : (
        <>
          <Paper component="main" sx={centeredCardSx}>
            <LoginHeader
              title={<Trans context="login header">Log in to Watershed</Trans>}
              orgName={metadata?.orgName}
              orgIconUrl={metadata?.orgIconUrl}
              error={
                // Don't actually show the error box if we send a permission request to org admins
                authError !== AuthError.PermissionsRequired
                  ? state.error
                    ? getAuthErrorMessageOrDefault(state.error)
                    : reasonMsg
                  : null
              }
            />
            <LoginMessages
              isTryingToAccessReport={isTryingToAccessReport}
              isTryingToAccessCustomerHub={isTryingToAccessCustomerHub}
              customerName={customerName}
              watershedGeneric={watershedGeneric}
              customerRelation={customerRelation}
            />
            {authError === AuthError.PermissionsRequired && (
              <Callout
                variant="info"
                sx={{ mb: 3, textAlign: 'center' }}
                description={
                  <Typography variant="body1">
                    {adminName && adminEmail ? (
                      <Trans context="Message shown when user needs admin approval, with admin name and email">
                        You don't currently have access to Watershed. We've
                        emailed {adminName} (
                        <TextLink href={`mailto:${adminEmail}`}>
                          {adminEmail}
                        </TextLink>
                        ) to approve your request for access.
                      </Trans>
                    ) : (
                      <Trans context="Message shown when user needs admin approval but no admin contact is available">
                        You don't currently have access to Watershed. Please
                        contact Watershed support at{' '}
                        <TextLink href={SUPPORT_EMAIL_LINK}>
                          {SUPPORT_EMAIL_ADDRESS}
                        </TextLink>{' '}
                        to request access.
                      </Trans>
                    )}
                  </Typography>
                }
              />
            )}
            <LoginComponent />
          </Paper>

          <Stack
            justifyContent="space-evenly"
            direction="row"
            sx={{
              py: 2,
              px: 4,
              gap: 2,
            }}
          >
            <LoginFooter />
          </Stack>
        </>
      )}
    </Box>
  );
}
